import * as twgl from "twgl.js";
import shaders from "./shaders";
import { TileManager } from "./tileManager";

/** Renders the atlas texture on a debug canvas. */
export class DebugRenderer {
    private debugGl: WebGL2RenderingContext;
    private tileManager: TileManager;
    private debugProgram: twgl.ProgramInfo;
    private debugQuadBuffer: twgl.BufferInfo;
    private debugAtlasTexture: WebGLTexture;
    private tileSize: number;
    private atlasSize: number;

    constructor(debugGl: WebGL2RenderingContext, tileManager: TileManager, tileSize: number, atlasSize: number) {
        this.debugGl = debugGl;
        this.tileManager = tileManager;
        this.tileSize = tileSize;
        this.atlasSize = atlasSize;
        this.debugProgram = twgl.createProgramInfo(debugGl, [shaders.vertDebug, shaders.fragDebug]);
        this.debugQuadBuffer = twgl.createBufferInfoFromArrays(debugGl, {
            a_position: { numComponents: 2, data: [-1, -1, 1, -1, 1, 1, -1, 1] },
            a_uv: { numComponents: 2, data: [0, 0, 1, 0, 1, 1, 0, 1] },
            indices: [0, 1, 2, 0, 2, 3],
        });
        this.debugAtlasTexture = debugGl.createTexture()!;
        debugGl.bindTexture(debugGl.TEXTURE_2D, this.debugAtlasTexture);
        debugGl.texParameteri(debugGl.TEXTURE_2D, debugGl.TEXTURE_MIN_FILTER, debugGl.LINEAR);
        debugGl.texParameteri(debugGl.TEXTURE_2D, debugGl.TEXTURE_MAG_FILTER, debugGl.LINEAR);
        debugGl.texImage2D(debugGl.TEXTURE_2D, 0, debugGl.RGBA, atlasSize, atlasSize, 0, debugGl.RGBA, debugGl.UNSIGNED_BYTE, null);
    }

    /** Renders the atlas texture on the debug canvas. */
    render(): void {
        const { debugGl, tileSize, atlasSize, debugProgram, debugQuadBuffer, debugAtlasTexture } = this;
        debugGl.viewport(0, 0, debugGl.canvas.width, debugGl.canvas.height);
        debugGl.useProgram(debugProgram.program);
        twgl.setBuffersAndAttributes(debugGl, debugProgram, debugQuadBuffer);
        twgl.setUniforms(debugProgram, {
            uAtlasTexture: debugAtlasTexture,
            uAtlasSize: atlasSize,
            uTileSize: [tileSize, tileSize],
            uShowGrid: 1.0,
        });
        twgl.drawBufferInfo(debugGl, debugQuadBuffer);
    }

    /** Updates the debug atlas texture with new tiles */
    async updateTexture(tileId: string, tilePath: string, i: number, j: number): Promise<void> {
        const { debugGl, debugAtlasTexture, tileSize, tileManager } = this;
        const image = await tileManager.retrieveTile(tileId, tilePath);
        const x = i * tileSize;
        const y = j * tileSize;
        debugGl.bindTexture(debugGl.TEXTURE_2D, debugAtlasTexture);
        debugGl.texSubImage2D(debugGl.TEXTURE_2D, 0, x, y, tileSize, tileSize, debugGl.RGBA, debugGl.UNSIGNED_BYTE, image);
    }
}